import axios from 'axios';

import apiClient from './apiClient';
import { getAuthToken, refreshToken as refreshAuthToken } from '../utils/auth';
import { getApiBaseUrl } from '../config/runtimeUrls';

export interface WhisperTranscriptionResponse {
  text: string;
  language?: string;
  confidence?: number;
  duration?: number;
}

export interface WhisperError {
  message: string;
  code?: string;
  status?: number;
}

interface WhisperServiceConfig {
  silenceTimeout: number; // milliseconds of silence before auto-stop
  maxRecordingTime: number; // maximum recording time in milliseconds
  chunkInterval: number; // milliseconds between audio chunks
  autoTranscribe: boolean; // whether to automatically transcribe after stopping
}

class WhisperService {
  private baseURL: string;
  private isRecording: boolean = false;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private config: WhisperServiceConfig;
  private silenceTimer: NodeJS.Timeout | null = null;
  private maxTimeTimer: NodeJS.Timeout | null = null;
  private lastAudioTime: number = 0;
  private onAutoStopCallback: ((audioBlob: Blob) => Promise<void>) | null = null;
  private authRetryCount: number = 0;
  private readonly maxAuthRetries: number = 2;

  constructor() {
    this.baseURL = getApiBaseUrl();
    this.config = {
      silenceTimeout: 2500, // 2.5 seconds of silence for faster response
      maxRecordingTime: 30000, // 30 seconds max
      chunkInterval: 1000, // 1 second chunks
      autoTranscribe: true,
    };
  }

  /**
   * Configure the WhisperService
   */
  configure(config: Partial<WhisperServiceConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('🎤 WhisperService: Configuration updated:', this.config);
  }

  /**
   * Set callback for automatic stop
   */
  setAutoStopCallback(callback: (audioBlob: Blob) => Promise<void>): void {
    this.onAutoStopCallback = callback;
    console.log('🎤 WhisperService: Auto-stop callback set');
  }

  /**
   * Start recording audio from microphone with automatic transcription
   */
  async startRecording(): Promise<void> {
    console.log('🎤 WhisperService: startRecording() called');
    console.log('🎤 WhisperService: Current recording state:', { isRecording: this.isRecording });

    try {
      console.log('🎤 WhisperService: Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // Optimal for Whisper
        },
      });
      console.log('🎤 WhisperService: Microphone access granted, stream received');

      this.audioChunks = [];
      this.isRecording = true;
      this.lastAudioTime = Date.now();
      console.log('🎤 WhisperService: Audio chunks cleared, isRecording set to true');

      // Create MediaRecorder with WebM format (supported by Whisper)
      console.log('🎤 WhisperService: Creating MediaRecorder...');
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });
      console.log('🎤 WhisperService: MediaRecorder created successfully');

      this.mediaRecorder.ondataavailable = event => {
        console.log('🎤 WhisperService: ondataavailable event fired, data size:', event.data.size);
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
          this.lastAudioTime = Date.now();
          console.log(
            '🎤 WhisperService: Audio chunk added, total chunks:',
            this.audioChunks.length
          );

          // Reset silence timer
          this.resetSilenceTimer();
        } else {
          console.log('🎤 WhisperService: Empty audio chunk received, skipping');
        }
      };

      this.mediaRecorder.onstop = () => {
        console.log('🎤 WhisperService: onstop event fired, stopping all tracks');
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('🎤 WhisperService: Track stopped:', track.kind);
        });

        // Clear timers
        this.clearTimers();
      };

      // Start recording
      console.log('🎤 WhisperService: Starting MediaRecorder...');
      this.mediaRecorder.start(this.config.chunkInterval);
      console.log('🎤 WhisperService: MediaRecorder started successfully');

      // Start silence detection
      this.startSilenceDetection();

      // Start max time timer
      this.startMaxTimeTimer();

      console.log('🎤 WhisperService: Recording started successfully with auto-stop enabled');
    } catch (error) {
      console.error('🎤 WhisperService: ❌ Error starting recording:', error);
      this.clearTimers();
      throw new Error('Failed to start audio recording. Please check microphone permissions.');
    }
  }

  /**
   * Start silence detection timer
   */
  private startSilenceDetection(): void {
    console.log(
      '🎤 WhisperService: Starting silence detection timer:',
      this.config.silenceTimeout,
      'ms'
    );
    this.silenceTimer = setTimeout(() => {
      if (this.isRecording) {
        console.log('🎤 WhisperService: Silence timeout reached, auto-stopping recording');
        this.autoStopRecording();
      }
    }, this.config.silenceTimeout);
  }

  /**
   * Reset silence detection timer
   */
  private resetSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
    }
    this.startSilenceDetection();
  }

  /**
   * Start maximum recording time timer
   */
  private startMaxTimeTimer(): void {
    console.log('🎤 WhisperService: Starting max time timer:', this.config.maxRecordingTime, 'ms');
    this.maxTimeTimer = setTimeout(() => {
      if (this.isRecording) {
        console.log('🎤 WhisperService: Max recording time reached, auto-stopping recording');
        this.autoStopRecording();
      }
    }, this.config.maxRecordingTime);
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.maxTimeTimer) {
      clearTimeout(this.maxTimeTimer);
      this.maxTimeTimer = null;
    }
  }

  /**
   * Automatically stop recording and process audio
   */
  private async autoStopRecording(): Promise<void> {
    console.log('🎤 WhisperService: Auto-stop recording triggered');

    try {
      const audioBlob = await this.stopRecording();

      if (audioBlob && audioBlob.size > 0) {
        console.log('🎤 WhisperService: Auto-stop: Audio blob created, size:', audioBlob.size);

        if (this.onAutoStopCallback) {
          console.log('🎤 WhisperService: Auto-stop: Calling callback with audio blob');
          await this.onAutoStopCallback(audioBlob);
        } else if (this.config.autoTranscribe) {
          console.log('🎤 WhisperService: Auto-stop: Auto-transcribing audio');
          const transcription = await this.transcribeAudio(audioBlob);
          console.log('🎤 WhisperService: Auto-stop: Transcription completed:', transcription);

          // Emit transcription event
          window.dispatchEvent(
            new CustomEvent('whisper-transcription-complete', {
              detail: { transcription, audioBlob },
            })
          );
        }
      } else {
        console.warn('🎤 WhisperService: Auto-stop: No audio data captured');
      }
    } catch (error) {
      console.error('🎤 WhisperService: Auto-stop: Error processing audio:', error);
    }
  }

  /**
   * Stop recording and return the audio blob
   */
  async stopRecording(): Promise<Blob | null> {
    console.log('🎤 WhisperService: stopRecording() called');
    console.log('🎤 WhisperService: Current state:', {
      isRecording: this.isRecording,
      mediaRecorder: !!this.mediaRecorder,
      audioChunks: this.audioChunks.length,
    });

    if (!this.mediaRecorder || !this.isRecording) {
      console.log('🎤 WhisperService: Not recording, returning null');
      return null;
    }

    return new Promise(resolve => {
      console.log('🎤 WhisperService: Setting up onstop handler...');
      this.mediaRecorder!.onstop = () => {
        console.log('🎤 WhisperService: onstop handler executed');
        this.isRecording = false;

        if (this.audioChunks.length === 0) {
          console.log('🎤 WhisperService: ⚠️ No audio data recorded');
          resolve(null);
          return;
        }

        // Create audio blob
        console.log(
          '🎤 WhisperService: Creating audio blob from',
          this.audioChunks.length,
          'chunks'
        );
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        console.log('🎤 WhisperService: Audio blob created, size:', audioBlob.size, 'bytes');

        resolve(audioBlob);
      };

      console.log('🎤 WhisperService: Stopping MediaRecorder...');
      this.mediaRecorder!.stop();
      console.log('🎤 WhisperService: MediaRecorder.stop() called');
    });
  }

  /**
   * Check if currently recording
   */
  isCurrentlyRecording(): boolean {
    return this.isRecording;
  }

  /**
   * Send audio to backend for Whisper transcription
   */
  async transcribeAudio(audioBlob: Blob): Promise<WhisperTranscriptionResponse> {
    console.log('🎤 WhisperService: transcribeAudio() called');
    console.log('🎤 WhisperService: Audio blob details:', {
      size: audioBlob.size,
      type: audioBlob.type,
    });

    try {
      // Prefer base64 path first to avoid multipart issues in some environments
      try {
        const client = apiClient.getClient();
        const base64 = await this.convertBlobToDataUrl(audioBlob);
        let alt = await client.post(
          '/whisper/transcribe-base64',
          { audioBase64: base64, mimeType: 'audio/webm' },
          {
            withCredentials: true,
            validateStatus: s => (s ?? 0) < 500,
            timeout: 30000,
          }
        );
        if (alt.status === 200) {
          console.log('🎤 WhisperService: ✅ Base64 transcription successful');
          this.authRetryCount = 0;
          return alt.data as WhisperTranscriptionResponse;
        }
        if (alt.status === 401 && this.authRetryCount < this.maxAuthRetries) {
          this.authRetryCount++;
          console.warn(
            `🎤 WhisperService: Base64 401 detected (attempt ${this.authRetryCount}/${this.maxAuthRetries})`
          );
          try {
            const newToken = await refreshAuthToken();
            if (newToken) {
              alt = await client.post(
                '/whisper/transcribe-base64',
                { audioBase64: base64, mimeType: 'audio/webm' },
                {
                  withCredentials: true,
                  headers: { Authorization: `Bearer ${newToken}` },
                  validateStatus: s => (s ?? 0) < 500,
                  timeout: 30000,
                }
              );
              if (alt.status === 200) {
                console.log('🎤 WhisperService: ✅ Base64 transcription successful after refresh');
                this.authRetryCount = 0;
                return alt.data as WhisperTranscriptionResponse;
              }
            }
          } catch (_) {}
        }
        console.warn(
          '🎤 WhisperService: Base64 transcription not accepted, falling back to multipart',
          alt.status
        );
      } catch (e) {
        console.warn('🎤 WhisperService: Base64 transcription failed, will try multipart next', e);
      }

      // Create FormData with audio file
      console.log('🎤 WhisperService: Creating FormData...');
      const formData = new FormData();
      // Ensure the file has a correct MIME type hint for Multer/OpenAI
      const file = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
      formData.append('audio', file);
      console.log('🎤 WhisperService: FormData created with audio file');
      // Send via centralized API client (handles auth, refresh, CORS, cookies)
      const client = apiClient.getClient();
      console.log('🎤 WhisperService: Making authenticated POST request via ApiClient...');
      const response = await client.post('/whisper/transcribe', formData, {
        // Let axios set multipart boundary automatically for FormData
        timeout: 30000,
        withCredentials: true,
        validateStatus: status => (status ?? 0) < 500,
      });

      if (response.status === 401 && this.authRetryCount < this.maxAuthRetries) {
        return await this.handleUnauthorizedError('/whisper/transcribe', formData);
      }

      if (response.status !== 200) {
        // Fallback: try base64 endpoint if server rejects multipart (e.g., proxies)
        if (response.status === 400) {
          try {
            const base64 = await this.convertBlobToDataUrl(file);
            const alt = await client.post(
              '/whisper/transcribe-base64',
              { audioBase64: base64, mimeType: file.type },
              {
                withCredentials: true,
                validateStatus: s => (s ?? 0) < 500,
                timeout: 30000,
              }
            );
            if (alt.status === 200) {
              this.authRetryCount = 0;
              return alt.data as WhisperTranscriptionResponse;
            }
          } catch (_) {
            // proceed to throw below
          }
        }
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }

      // Reset retry counter on success
      this.authRetryCount = 0;
      console.log('🎤 WhisperService: ✅ Transcription successful via ApiClient');
      return response.data as WhisperTranscriptionResponse;
    } catch (error: any) {
      return this.handleTranscriptionError(error, audioBlob);
    }
  }

  private async convertBlobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Handle 401 Unauthorized with token refresh and retry
   */
  private async handleUnauthorizedError(
    endpoint: string,
    formData: FormData
  ): Promise<WhisperTranscriptionResponse> {
    this.authRetryCount++;
    console.warn(
      `🎤 WhisperService: 401 detected (attempt ${this.authRetryCount}/${this.maxAuthRetries})`
    );

    try {
      const newToken = await refreshAuthToken();
      if (!newToken) throw new Error('Token refresh failed');

      const client = apiClient.getClient();
      const retryResponse = await client.post(endpoint, formData, {
        // Authorization header will be set by apiClient interceptor as well; include explicitly as safeguard
        headers: { Authorization: `Bearer ${newToken}` },
        timeout: 30000,
        withCredentials: true,
      });

      if (retryResponse.status !== 200) {
        throw new Error(`Retry failed with ${retryResponse.status}`);
      }

      this.authRetryCount = 0;
      return retryResponse.data as WhisperTranscriptionResponse;
    } catch (err) {
      throw new Error('Authentication failed. Please log in again.');
    }
  }

  /**
   * Enterprise-grade error handling for STT
   */
  private handleTranscriptionError(error: any, audioBlob: Blob): never {
    console.error('🎤 WhisperService: ❌ Request failed:', {
      message: error?.message,
      code: error?.code,
      status: error?.response?.status,
      config: error?.config,
    });

    if (error?.code === 'ECONNABORTED') {
      throw new Error('Transcription timed out. Please try again.');
    }
    const status = error?.response?.status;
    switch (status) {
      case 401:
        throw new Error('Session expired. Please log in again.');
      case 413:
        throw new Error(`Audio file too large (${audioBlob.size} bytes). Limit: 25MB`);
      case 400:
        throw new Error('Invalid audio format. Please use WebM/Opus');
      case 429:
        throw new Error('Too many requests. Please wait and try again.');
      default:
        throw new Error('Transcription service unavailable. Try again later.');
    }
  }

  /**
   * Complete workflow: record audio and get transcription
   */
  async recordAndTranscribe(): Promise<WhisperTranscriptionResponse> {
    // Start recording
    await this.startRecording();

    // Wait for user to stop recording (this would be triggered by UI)
    // For now, we'll return a promise that the UI can resolve
    return new Promise((resolve, reject) => {
      // Store the resolve/reject functions for the UI to call
      (window as any).whisperResolve = resolve;
      (window as any).whisperReject = reject;
    });
  }

  /**
   * Complete the recording and transcription process
   */
  async completeRecording(): Promise<WhisperTranscriptionResponse> {
    console.log('🎤 WhisperService: completeRecording() called');
    console.log('🎤 WhisperService: Current recording state:', { isRecording: this.isRecording });

    try {
      // Stop recording
      console.log('🎤 WhisperService: About to call stopRecording()');
      const audioBlob = await this.stopRecording();
      console.log('🎤 WhisperService: stopRecording() completed, audioBlob:', !!audioBlob);

      if (!audioBlob) {
        console.error('🎤 WhisperService: ❌ No audio recorded. Please try again.');
        throw new Error('No audio recorded. Please try again.');
      }

      // Check file size (Whisper has a 25MB limit)
      console.log('🎤 WhisperService: Checking audio file size:', audioBlob.size, 'bytes');
      if (audioBlob.size > 25 * 1024 * 1024) {
        console.error('🎤 WhisperService: ❌ Audio file too large:', audioBlob.size, 'bytes');
        throw new Error('Audio file too large. Please record a shorter message.');
      }
      console.log('🎤 WhisperService: Audio file size is within limits');

      // Transcribe audio
      console.log('🎤 WhisperService: About to call transcribeAudio()');
      const transcription = await this.transcribeAudio(audioBlob);
      console.log('🎤 WhisperService: transcribeAudio() completed successfully');
      console.log('🎤 WhisperService: Transcription result:', transcription);

      return transcription;
    } catch (error) {
      console.error('🎤 WhisperService: ❌ Error in completeRecording():', error);
      throw error;
    }
  }

  /**
   * Get authentication token from localStorage or other storage
   */
  private getAuthToken(): string | null {
    // Use centralized auth utility
    return getAuthToken();
  }

  /**
   * Check if Whisper is available (microphone permissions, etc.)
   */
  async checkAvailability(): Promise<{ available: boolean; error?: string }> {
    console.log('🎤 WhisperService: checkAvailability() called');

    try {
      // Check if MediaRecorder is supported
      console.log('🎤 WhisperService: Checking MediaRecorder support...');
      if (!window.MediaRecorder) {
        console.error('🎤 WhisperService: ❌ MediaRecorder not supported');
        return { available: false, error: 'MediaRecorder not supported in this browser' };
      }
      console.log('🎤 WhisperService: ✅ MediaRecorder is supported');

      // Check if WebM is supported
      console.log('🎤 WhisperService: Checking WebM audio format support...');
      const supportedTypes = MediaRecorder.isTypeSupported('audio/webm;codecs=opus');
      if (!supportedTypes) {
        console.error('🎤 WhisperService: ❌ WebM audio format not supported');
        return { available: false, error: 'WebM audio format not supported' };
      }
      console.log('🎤 WhisperService: ✅ WebM audio format is supported');

      // Check microphone permissions
      console.log('🎤 WhisperService: Checking microphone permissions...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('🎤 WhisperService: ✅ Microphone access granted');

      // Stop the test stream
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('🎤 WhisperService: Test track stopped:', track.kind);
      });

      console.log('🎤 WhisperService: ✅ All availability checks passed');
      return { available: true };
    } catch (error: any) {
      console.error('🎤 WhisperService: ❌ Availability check failed:', error);

      if (error.name === 'NotAllowedError') {
        console.error('🎤 WhisperService: Microphone permission denied');
        return { available: false, error: 'Microphone permission denied' };
      } else if (error.name === 'NotFoundError') {
        console.error('🎤 WhisperService: No microphone found');
        return { available: false, error: 'No microphone found' };
      } else {
        console.error('🎤 WhisperService: Microphone not available:', error.message);
        return { available: false, error: 'Microphone not available' };
      }
    }
  }

  /**
   * Get recording status
   */
  getRecordingStatus(): { isRecording: boolean; duration?: number } {
    return {
      isRecording: this.isRecording,
      // Could add duration tracking here if needed
    };
  }
}

// Create singleton instance
const whisperService = new WhisperService();

export default whisperService;
